// @ts-strict-ignore
import { AsyncPipe, KeyValuePipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EmbeddedViewRef,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { LetDirective } from '@ngrx/component';
import { createSelector, MemoizedSelector, Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { first, switchMap, takeUntil, tap } from 'rxjs/operators';

import { StageV2, StageV2ViewConfiguration, StageView, Vehicle } from 'src/app/api/owcc/models';
import { AppDomContextService } from 'src/app/app-dom-context.service';
import { Mq } from 'src/app/core/config/mq.config';
import {
  updateInterior360DataUrl,
  updateInteriorParameter,
  updateStageView,
  updateVehicleImageUrl
} from 'src/app/store/common/common.actions';
import {
  fromCarl,
  fromMq,
  selectCommonStageV2,
  selectCommonStageV2ViewConfiguration,
  selectConfigurationVehicle,
  selectDisplayShadowUnderStage,
  selectIsSummarySidebarShown,
  selectSelectableCategoryById
} from 'src/app/store/common/common.selectors';
import { MbccState } from 'src/app/store/mbcc/mbcc.state';
import {
  selectConfigurationStep,
  selectIsMotorizationOrSummary,
  selectIsMotorizationPage,
  selectIsSummaryPage
} from 'src/app/store/router/router.selectors';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';
import { MAX_IMAGE_WIDTH } from 'src/app/utils/global-constants';

import { AwesomeStageComponent } from '../awesome-stage/awesome-stage.component';
import {
  InteriorParameter,
  StageBaseData,
  StageConfig,
  StageViewStateValue
} from '../awesome-stage/awesome-stage.model';
import { BbdUrlBuilder } from '../awesome-stage/bbd-url-builder';

import { stageBackgroundCroppingData } from './main-stage.cropping-data';
import { MainStageStore } from './main-stage.store';

function insertChildAtIndex(element: Element, child: Element, index: number): void {
  if (!index) {
    index = 0;
  }
  if (index >= element.children.length) {
    element.append(child);
  } else {
    element.insertBefore(child, element.children[index]);
  }
}

export interface Stickiness {
  isSticky: boolean;
  topPosition: number;
  height: number;
}

type MainStageConfig = StageConfig;

const DEFAULT_MAIN_STAGE_CONFIG: StageConfig = {
  exterior: {
    threeSixtyPreloadHighResImages: false,
    threeSixtyRotationSpeedFactor: 1,
    scaleFactor: 1.1,
    hasSmoothRotation: true,
    imagePreloading: {
      concurrency: 5,
      initLookaheadEachSide: 5,
      rotationLookahead: 10
    },
    rotation: {
      baseDelay: 40,
      rotationSlowdown: 20,
      slowdownReverseIndex: 4
    }
  },
  disclaimer: ''
};

const createFallbackViewConfiguration = (vehicle: Vehicle): StageV2ViewConfiguration => {
  const initialModeConfig = vehicle?.stageV2?.metaData?.views?.[StageView.Exterior360]?.initialModeConfig;
  return {
    perspective: initialModeConfig?.perspective,
    state: initialModeConfig?.state,
    view: StageView.Exterior360
  } as StageV2ViewConfiguration;
};

const selectStageUpdateDataByConfigurationStep = (
  step: string
): MemoizedSelector<MbccState, StageV2ViewConfiguration> =>
  createSelector(
    selectCommonStageV2ViewConfiguration,
    selectSelectableCategoryById(step.toUpperCase()),
    selectConfigurationVehicle,
    (manuallySetStageViewConfiguration, vehicleComponentCategory, vehicle) =>
      manuallySetStageViewConfiguration ||
      vehicleComponentCategory?.categoryViewConfiguration?.stageV2 ||
      createFallbackViewConfiguration(vehicle)
  );

const selectIsSummaryAndMq3Max = createSelector(
  selectIsSummaryPage,
  fromMq.selectIsMq3Max,
  (isSummaryPage, isMq3Max) => ({
    isSummaryPage,
    isMq3Max
  })
);

/**
 * Main stage component which encapsulate the actual stage component.
 * Intentionally separated for reasons, e.g. to handle the background which overlays the stage (full browser width).
 */
@Component({
  selector: 'cc-main-stage',
  templateUrl: './main-stage.component.html',
  styleUrl: './main-stage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MainStageStore],
  imports: [AwesomeStageComponent, NgStyle, NgClass, LetDirective, AsyncPipe, NgTemplateOutlet, KeyValuePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MainStageComponent implements AfterViewInit, OnDestroy {
  /**
   * Query selector which the background (defined in this component as a template) should be teleported to.
   */
  @Input() public backgroundTeleportSelector = '';

  /**
   * Reference to a template which contains the template.
   * Used to attach to a certain place - depending on the injected query selector.
   */
  @ViewChild('backgroundTemplate', { read: TemplateRef }) public backgroundTemplate: TemplateRef<Element>;

  public destroy$: Subject<void> = new Subject<void>();

  public cropping = stageBackgroundCroppingData;

  public readonly isContentScrolledDown$ = this.store.select(selectDisplayShadowUnderStage);

  public readonly stickinessStyle$ = this.mainStageStore.stickinessStyle$;

  public readonly stageBackgroundUrl$ = this.mainStageStore.stageBackgroundUrl$;

  public readonly stageBackgroundId$ = this.mainStageStore.stageBackgroundId$;

  public readonly isStageViewInterior$ = this.mainStageStore.isStageViewInterior$;

  public readonly mediaQuery$: Observable<Mq> = this.store.select(fromMq.selectMediaQuery);

  public readonly stageBackgroundHeight$ = this.mainStageStore.stageBackgroundHeight$;

  public readonly buttonTooltipTexts$ = this.mainStageStore.buttonTooltipTexts$;

  public readonly stageConfig$: Observable<MainStageConfig> = this.store.select(
    fromCarl.selectConfig<MainStageConfig>('mainStage', DEFAULT_MAIN_STAGE_CONFIG)
  );

  public readonly stageBaseData$ = this.store.select<StageBaseData>(selectCommonStageV2).pipe(
    filterTruthy(),
    tap((stageBaseData: StageV2) => {
      this.mainStageStore.updateStageBackgrounds(stageBaseData?.metaData?.baseInfo?.backgrounds || []);
    })
  );

  public readonly stageUpdateData$: Observable<StageV2ViewConfiguration> = this.store
    .select(selectConfigurationStep)
    .pipe(
      filterTruthy(),
      switchMap(step => combineLatest([of(step), this.store.select(selectIsMotorizationOrSummary)])),
      switchMap(([step, isMotorizationOrSummary]) =>
        // empty string for motorization/summary page as they're added manually, so the fallback will be used
        this.store.select(selectStageUpdateDataByConfigurationStep(isMotorizationOrSummary ? '' : step))
      )
    );

  public readonly selectIsMotorizationAndDesktop = createSelector(
    selectIsMotorizationPage,
    fromMq.selectIsMq4Min,
    (isMotorization, isMq4Min) => isMotorization && isMq4Min
  );

  public readonly isMotorizationAndDesktop$ = this.store.select(this.selectIsMotorizationAndDesktop);

  public readonly isSidebarShown$: Observable<boolean> = this.store.select(selectIsSummarySidebarShown);

  public constructor(
    private readonly store: Store,
    private readonly mainStageStore: MainStageStore,
    private readonly appDomContextService: AppDomContextService,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {}

  /**
   * Optional stickiness info to make the main stage sticky (if needed).
   */
  @Input() public set stickiness(stickiness: Stickiness | undefined) {
    this.mainStageStore.updateStickiness(stickiness);
  }

  /**
   * Set to true if a selection box (e.g. for selection colors or trims) is next to it.
   * Stage ratio is derived from this information.
   */
  @Input() public set hasSelectionBoxLeftNextToIt(hasSelectionBoxLeftNextToIt: boolean) {
    this.mainStageStore.updateHasSelectionBoxLeftNextToIt(hasSelectionBoxLeftNextToIt);
  }

  public ngAfterViewInit(): void {
    const nodesToRemove: Element[] = [];

    const cleanupOnDestroy = (nodesForRemoval: Element[]): void => {
      combineLatest([of(nodesForRemoval), this.destroy$])
        .pipe(first())
        .subscribe(([nodes]) => {
          for (const node of nodes) node.remove();
        });
    };

    const cleanupOnMqChanged = (nodesForRemoval: Element[]): void => {
      for (const node of nodesForRemoval) node.remove();
    };

    const teleportBackgroundToStage = (selector: string): void => {
      if (selector) {
        const embeddedView: EmbeddedViewRef<Element> = this.viewContainerRef.createEmbeddedView(
          this.backgroundTemplate
        );
        const mainElement: HTMLElement = this.appDomContextService.shadowRoot?.querySelector(selector);
        for (const node of embeddedView.rootNodes as Element[]) {
          insertChildAtIndex(mainElement, node, 0);
          nodesToRemove.push(node);
        }

        cleanupOnDestroy(nodesToRemove);
      }
    };

    this.store
      .select(selectIsSummaryAndMq3Max)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ isSummaryPage, isMq3Max }) => {
        const showBackground = !isSummaryPage || isMq3Max;

        if (showBackground) {
          teleportBackgroundToStage(this.backgroundTeleportSelector);
        }

        if (!showBackground) {
          cleanupOnMqChanged(nodesToRemove);
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onInterior360DataUrlChange(interior360DataUrl: string): void {
    this.store.dispatch(updateInterior360DataUrl({ interior360DataUrl }));
  }

  public onInteriorParameterChange(interiorParameter: InteriorParameter): void {
    this.store.dispatch(updateInteriorParameter({ interiorParameter }));
  }

  public onStageViewChange(stageView: StageView): void {
    this.store.dispatch(updateStageView({ stageView }));
  }

  public onStageViewStateChange(stageViewState: StageViewStateValue): void {
    this.mainStageStore.updateStageViewState(stageViewState);
  }

  public updateStageBackgroundHeight(stageHeight: number): void {
    this.mainStageStore.updateStageBackgroundHeight({ stageHeight, element: this.elementRef });
  }

  public onFullscreenModeChange(isInFullscreenMode: boolean): void {
    this.mainStageStore.updateFullscreenMode(isInFullscreenMode);
  }

  /**
   * Update image url in the (global) store to make use of it in the NBAs (save image).
   */
  public updateImageUrl(vehicleImageUrl: string): void {
    const fullResolutionVehicleImageUrl = new BbdUrlBuilder(vehicleImageUrl)
      .patchImgWidth(MAX_IMAGE_WIDTH.toString())
      .toString();
    this.store.dispatch(updateVehicleImageUrl({ vehicleImageUrl: fullResolutionVehicleImageUrl }));
  }
}
